import { DependentResourceDc, ExtendedProjectInfoDc, unique, UsedProjectDc } from "@evergis/api";
import { createMutation, createResource } from "@evergis/react-fetching";

import { getTablesRefs, isProjectRef } from "./tables";
import { promiseAllSettled } from "../utils/promiseAllSettled";
import { api as evergisApi } from "../evergis/api";
import { ProjectInfo } from "../interfaces/resourceInfo";
import { ResourcesFetchParams } from "../modules/Catalog/hooks/useFetchParams";

export type ProjectRefsWithTablesInput = {
  names: string[];
  extendedRefsLimit: number;
};

export const projects = createResource(
  ({ category, ...params }: ResourcesFetchParams) =>
    async api => {
      return {
        data: await api.projects.findAll(params),
      };
    },
  { injected: evergisApi },
);

export const project = createResource(
  (name: string) => async api => ({
    data: await api.projects.findOne(name),
  }),
  {
    injected: evergisApi,
    getKey: input => input,
  },
);

export const createProject = createMutation(
  (info: ExtendedProjectInfoDc) => async api => ({
    data: await api.projects.create(info),
  }),
  {
    injected: evergisApi,
    update: () => projects.invalidateAll(),
  },
);

export const projectInfosWithDeps = createResource(
  (names: string[]) => async api => ({
    data: await api.resources.getProjectInfosWithDeps(names),
  }),
  { injected: evergisApi },
);

export const updateProject = createMutation(
  (info: ProjectInfo) => async api => {
    return {
      data: await api.projects.update({ ...info, layersCount: info.layersCount || 0 } as ExtendedProjectInfoDc),
    };
  },
  {
    injected: evergisApi,
    update: updatedProject => {
      project.write({
        input: updatedProject.name,
        reducer: value => ({ ...value, ...updatedProject }),
      });
    },
  },
);

export const lastProjectSave = createMutation(
  (projectName: string) => async api => ({
    data: await api.account.postUsedProjects({
      name: projectName,
    }),
  }),
  {
    injected: evergisApi,
    update: () => projectInfosWithDeps.invalidateAll(),
  },
);

export const lastProjects = createMutation(
  () => async api => {
    let projectsList: UsedProjectDc[] | null = null;

    try {
      projectsList = await api.account.getUsedProjects();
    } catch {
      // ...
    }

    return {
      data: projectsList,
    };
  },
  { injected: evergisApi },
);

export const projectsRefsWithTables = createResource(
  ({ names, extendedRefsLimit }: ProjectRefsWithTablesInput) =>
    async api => {
      const projectsRefs = await getTablesRefs(names, isProjectRef);

      const { username } = api.account;
      const myProjectsRefs = projectsRefs.filter(name => name.startsWith(username));
      const otherProjectsRefs = projectsRefs.filter(name => !name.startsWith(username));

      return {
        data: {
          my: [
            ...(await promiseAllSettled(
              myProjectsRefs.slice(0, extendedRefsLimit).map(name => api.projects.getProjectInfo(name)),
            )),
            ...myProjectsRefs.slice(extendedRefsLimit, myProjectsRefs.length),
          ],
          others: otherProjectsRefs,
        },
      };
    },
  { injected: evergisApi },
);

export async function getLayersRefs(tableNames: string[], filter: (ref: DependentResourceDc) => boolean) {
  const results = await promiseAllSettled(tableNames.map(name => getLayerRefs(name, filter)));

  return results.flat().filter(unique);
}

export async function getLayerRefs(tableName: string, filter: (ref: DependentResourceDc) => boolean) {
  const refs = await evergisApi.layers.getResourceReferences(tableName);

  return refs.dependencies.filter(filter).map(({ name }) => name);
}

export const projectsRefsWithLayers = createResource(
  ({ names, extendedRefsLimit }: ProjectRefsWithTablesInput) =>
    async api => {
      const projectsRefs = await getLayersRefs(names, isProjectRef);

      const { username } = api.account;
      const myProjectsRefs = projectsRefs.filter(name => name.startsWith(username));
      const otherProjectsRefs = projectsRefs.filter(name => !name.startsWith(username));

      return {
        data: {
          my: [
            ...(await promiseAllSettled(
              myProjectsRefs.slice(0, extendedRefsLimit).map(name => api.projects.getProjectInfo(name)),
            )),
            ...myProjectsRefs.slice(extendedRefsLimit, myProjectsRefs.length),
          ],
          others: otherProjectsRefs,
        },
      };
    },
  { injected: evergisApi },
);
